import { classNames } from "../utils/classNames";
import { Icon, IconProps } from "./Icon";
import { Listbox } from "@headlessui/react";
import { observer } from "mobx-react";
import { FloatingOverlay } from "./FloatingOverlay";
import { forwardRef, HTMLAttributes, ReactElement, useContext, useEffect, useRef } from "react";
import { paddingXMapping } from "./tailwindMappings";
import { renderIcon } from "./iconHelpers";
import { FloatingDropdownParams, useFloatingDropdown } from "./floatingHelpers";
import { useElementId } from "../utils/reactHelpers";
import { isTMessage, isTMessageParam, TMessage, TMessageParam } from "../i18n/tt";
import { when } from "../utils/functions";
import {
  DCFocusEventHandler,
  useDispatch,
  useFocusEventHandler,
  useTranslation,
} from "../stores/RootStoreContext";
import { DCEvent } from "../events/eventsDomain";
import { RadioFieldModel } from "../common/commonForms";
import { TableContext } from "./TableContext";
import { IconName } from "./iconMapping";
import { BgColor, bgColorClassName } from "./colors";

export type SelectProps<T> = {
  id?: string;
  testId?: string;
  label?: string | TMessage;
  placeholder?: string;
  disabled?: boolean;
  icon?: IconProps["name"];
  name?: string;
  value?: T;
  items: T[];
  onSelect: (item: T) => void | DCEvent[];
  onFocus?: DCFocusEventHandler;
  onBlur?: DCFocusEventHandler;
  idFn: (item: T) => string;
  itemLabelFn?: (item: T) => SelectItemProps["label"];
  labelFn: (item: T) => SelectItemProps["label"];
  iconFn?: (item: T) => SelectItemProps["icon"];
  captionFn?: (item: T) => SelectItemProps["caption"];
  fullWidth?: boolean;
  variant?: "in-table";
  compact?: boolean;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  iconOnly?: boolean;
  tooltip?: TMessage | string;
  rounded?: boolean | "left" | "right";
  grow?: boolean;
  bg?: BgColor;
  border?: boolean;
  className?: string;
  labelSrOnly?: boolean;
  dropdownOptions?: FloatingDropdownParams;
};

type SelectButtonProps<T> = SelectProps<T> & {
  open: boolean;
  buttonRef: (el: HTMLButtonElement | null) => void;
};

function SelectButton<T>({
  placeholder,
  disabled,
  icon,
  value,
  labelFn,
  captionFn,
  iconFn,
  open,
  variant,
  testId,
  buttonRef,
  fullWidth,
  label,
  name,
  error,
  message,
  iconOnly,
  tooltip,
  grow = true,
  bg = "default",
  border = true,
  rounded = true,
  className,
  labelSrOnly,
  ...props
}: SelectButtonProps<T>) {
  const { tf } = useTranslation();
  const id = useElementId(props.id);
  const tableContext = useContext(TableContext);
  const inTable = variant === "in-table" || tableContext?.editable;
  const compact = props.compact || tableContext?.compact;
  const valueLabel = value !== undefined ? labelFn(value) : undefined;
  const caption = value !== undefined ? captionFn?.(value) : undefined;
  const valueIcon = value !== undefined && iconFn ? iconFn(value) : undefined;
  const focusHandler = useFocusEventHandler(props.onFocus);
  const blurHandler = useFocusEventHandler(props.onBlur);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasBeenOpened = useRef<boolean>(false);

  useEffect(() => {
    if (open) {
      hasBeenOpened.current = true;
    }
  }, [open]);

  useEffect(() => {
    if (hasBeenOpened.current && !open) {
      // Restore focus to the button when the dropdown is closed
      // HeadlessUI handles this when clicking outside the dropdown, but not if the user presses ESC
      containerRef.current?.querySelector("button")?.focus();
    }
  }, [open]);

  tooltip = typeof tooltip === "string" ? tooltip : tooltip ? tf(tooltip) : undefined;
  return (
    <div
      ref={containerRef}
      data-test-type={"select-label"}
      className={classNames(
        "tw-min-w-0",
        "tw-flex",
        "tw-flex-col",
        grow && "tw-grow",
        fullWidth && "tw-w-full",
      )}
      aria-label={tooltip}
      data-tooltip={tooltip}
    >
      {label && (
        <label
          htmlFor={id}
          className={classNames("tw-block tw-mb-1", (inTable || labelSrOnly) && "tw-sr-only")}
        >
          {typeof label === "string" ? label : tf(label)}
        </label>
      )}
      <Listbox.Button
        ref={buttonRef}
        id={id}
        name={name}
        data-testid={testId}
        data-test-type={"select"}
        className={classNames(
          className,
          "tw-flex",
          "tw-items-center",
          compact ? "tw-px-1" : "tw-px-4",
          "tw-leading-tight",
          border && !inTable && "tw-border",
          !inTable &&
            (rounded === "left"
              ? "tw-rounded-l"
              : rounded === "right"
                ? "tw-rounded-r"
                : "tw-rounded"),
          "focus:tw-ring-1",
          "tw-outline-none",
          "tw-cursor-pointer",
          disabled
            ? ["subtle", "bg-gray-x-soft", "border-gray-soft", "hover:border-gray-soft"]
            : error
              ? [
                  "color-default",
                  bgColorClassName(bg),
                  "border-error",
                  "focus-within:border-error",
                  "focus-within:ring-error",
                  "hover:border-error",
                  "hover:focus-within:border-error",
                ]
              : [
                  "color-default",
                  bgColorClassName(bg),
                  "border-gray",
                  "focus-within:border-primary",
                  "focus-within:ring-primary",
                  "hover:bg-border-gray-hard",
                  "hover:focus-within:border-primary",
                ],
        )}
        onClick={(e) => e.stopPropagation()}
        onFocus={focusHandler}
        onBlur={open ? undefined : blurHandler}
      >
        {when(valueIcon || icon, (i) => (
          <div className={classNames("text-xl tw-leading-5 tw-h-5 subtle tw-flex tw-items-center")}>
            {renderIcon(i)}
          </div>
        ))}
        {iconOnly && <span className={compact ? "tw-py-1" : "tw-py-3"}>&nbsp;</span>}
        <div
          className={classNames(
            iconOnly && "tw-sr-only",
            "tw-flex-1",
            "focus-visible:tw-outline-none",
            compact ? "tw-py-1" : "tw-py-3",
            valueIcon || icon ? "tw-mx-2" : "tw-mr-2",
            "bg-transparent",
            "disabled:bg-transparent",
            "tw-text-left",
            "tw-truncate",
          )}
        >
          {valueLabel ? (
            isTMessage(valueLabel) || isTMessageParam(valueLabel) ? (
              tf(valueLabel)
            ) : (
              valueLabel
            )
          ) : placeholder ? (
            <div className={"subtle"}>{placeholder}</div>
          ) : (
            <>&nbsp;</>
          )}
        </div>
        {caption && (
          <div className={classNames("subtle tw-mr-2", iconOnly && "tw-sr-only")}>
            {isTMessage(caption) || isTMessageParam(caption) ? tf(caption) : caption}
          </div>
        )}
        <div
          className={classNames("text-xl tw-leading-5 tw-h-5 tw-pointer-events-none tw-flex-none")}
        >
          <Icon name={open ? "caret-up" : "caret-down"} />
        </div>
      </Listbox.Button>
      {message && (
        <div
          className={classNames("tw-pt-1", "tw-pl-4", "text-sm", error ? "color-error" : "subtle")}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export type SelectItemProps = {
  label: ReactElement | TMessage | TMessageParam | string | number;
  caption?: ReactElement | TMessage | TMessageParam | string | number;
  icon?: ReactElement | IconProps | IconName;
  active?: boolean;
  isSelected?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(function SelectItem(
  { label, icon, caption, active, ...props }: SelectItemProps,
  ref,
) {
  const { tf } = useTranslation();
  return (
    <div
      {...props}
      ref={ref}
      className={classNames(
        props.className,
        paddingXMapping["sm"],
        "tw-flex",
        "tw-gap-2",
        "tw-items-center",
        "tw-py-2",
        "tw-cursor-pointer",
        "tw-min-w-[10rem]",
        "color-default",
        active ? "bg-primary-light" : "bg-default",
      )}
    >
      {icon && renderIcon(icon)}
      {isTMessage(label) || isTMessageParam(label) ? tf(label) : label}
      {caption && (
        <div className={"subtle tw-ml-auto"}>
          {isTMessage(caption) || isTMessageParam(caption) ? tf(caption) : caption}
        </div>
      )}
    </div>
  );
});

export const Select = observer(<T,>(props: SelectProps<T>) => {
  const { refs, floatingStyles } = useFloatingDropdown(props.dropdownOptions);
  const dispatch = useDispatch();

  return (
    <Listbox
      value={props.value}
      onChange={(item) => when(props.onSelect(item), dispatch)}
      disabled={props.disabled}
    >
      {({ open }) => (
        <>
          <SelectButton
            buttonRef={(ref) => {
              refs.setReference(ref);
              if (props.autofocus) {
                ref?.click();
              }
            }}
            open={open}
            {...props}
          />
          {open && (
            <FloatingOverlay layer={"dropdown"} modal>
              <Listbox.Options
                static
                as="div"
                ref={refs.setFloating}
                className={classNames(
                  "tw-rounded",
                  "tw-border",
                  "border-gray",
                  "tw-shadow",
                  "bg-default",
                  "tw-inline-flex",
                  "tw-flex-col",
                  "tw-py-1",
                  "tw-overflow-auto",
                )}
                style={floatingStyles}
              >
                {props.items.map((item) => (
                  <Listbox.Option
                    as="div"
                    key={props.idFn(item)}
                    value={item}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {({ active }) => (
                      <SelectItem
                        label={props.itemLabelFn ? props.itemLabelFn(item) : props.labelFn(item)}
                        icon={props.iconFn?.(item)}
                        caption={props.captionFn?.(item)}
                        active={active}
                      />
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </FloatingOverlay>
          )}
        </>
      )}
    </Listbox>
  );
});

export type MSelectProps<T> = {
  model: RadioFieldModel<T>;
  name?: SelectProps<T>["name"];
  icon?: SelectProps<T>["icon"];
  label?: SelectProps<T>["label"];
  placeholder?: SelectProps<T>["placeholder"];
  onSelect?: SelectProps<T>["onSelect"];
  onFocus?: SelectProps<T>["onFocus"];
  onBlur?: SelectProps<T>["onBlur"];
  idFn?: SelectProps<T>["idFn"];
  labelFn?: (item: T) => TMessage;
  iconFn?: SelectProps<T>["iconFn"];
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
  testId?: string;
  error?: boolean;
  message?: string;
  autofocus?: boolean;
  compact?: boolean;
  tooltip?: TMessage | string;
};

export const MSelect = observer(function MSelect<T>({
  model,
  onSelect,
  ...props
}: MSelectProps<T>) {
  const { t } = useTranslation();
  return (
    <Select
      {...props}
      value={model.value}
      items={model.options}
      onSelect={onSelect ?? model.setValue}
      idFn={model.getId ?? props.idFn}
      labelFn={props.labelFn ?? model.params.labelFn}
      iconFn={props.iconFn ?? model.params.iconFn}
      captionFn={model.params.descriptionFn}
      disabled={props.disabled}
      error={!model.isValid}
      message={when(model.error, t)}
    />
  );
});
